<template>
  <BaseModal
    :is-open="isOpen"
    title="Confirm Going Out"
    max-width="600px"
    @close="handleCancel"
  >
    <div class="go-out-container">
      <!-- Warning Icon -->
      <div class="warning-icon">
        <span class="material-symbols-outlined">warning</span>
      </div>

      <!-- Main Message -->
      <p class="main-message">
        You are about to end the round by going out. Please confirm you meet all requirements:
      </p>

      <!-- Requirements Checklist -->
      <div class="requirements-checklist">
        <h4 class="checklist-title">
          <span class="material-symbols-outlined">checklist</span>
          <span>Going Out Requirements</span>
        </h4>

        <div class="requirement-item" :class="{ met: requirements.naturalCanastas }">
          <span class="checkbox-icon material-symbols-outlined">
            {{ requirements.naturalCanastas ? 'check_circle' : 'cancel' }}
          </span>
          <div class="requirement-text">
            <span class="requirement-label">2 Natural Canastas</span>
            <span class="requirement-status">
              {{ teamCanastas.natural }} / 2
              {{ requirements.naturalCanastas ? '✓' : '' }}
            </span>
          </div>
        </div>

        <div class="requirement-item" :class="{ met: requirements.mixedCanastas }">
          <span class="checkbox-icon material-symbols-outlined">
            {{ requirements.mixedCanastas ? 'check_circle' : 'cancel' }}
          </span>
          <div class="requirement-text">
            <span class="requirement-label">2 Mixed Canastas</span>
            <span class="requirement-status">
              {{ teamCanastas.mixed }} / 2
              {{ requirements.mixedCanastas ? '✓' : '' }}
            </span>
          </div>
        </div>

        <div class="requirement-item" :class="{ met: requirements.emptyHand }">
          <span class="checkbox-icon material-symbols-outlined">
            {{ requirements.emptyHand ? 'check_circle' : 'cancel' }}
          </span>
          <div class="requirement-text">
            <span class="requirement-label">Empty Hand & Foot</span>
            <span class="requirement-status">
              {{ handCardCount }} cards remaining
              {{ requirements.emptyHand ? '✓' : '' }}
            </span>
          </div>
        </div>

        <div class="requirement-item" :class="{ met: requirements.noHiddenCanasta }">
          <span class="checkbox-icon material-symbols-outlined">
            {{ requirements.noHiddenCanasta ? 'check_circle' : 'cancel' }}
          </span>
          <div class="requirement-text">
            <span class="requirement-label">No Hidden Canasta</span>
            <span class="requirement-status">
              {{ hasHiddenCanasta ? 'Must play hidden canasta first' : '✓' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Warnings -->
      <div v-if="!allRequirementsMet" class="warnings-section">
        <div class="warning-item">
          <span class="material-symbols-outlined">error</span>
          <span>
            You do not meet all requirements to go out. Attempting to go out will result in an
            error.
          </span>
        </div>
      </div>

      <!-- Confirmation Info -->
      <div class="confirmation-info">
        <p class="info-text">
          <span class="material-symbols-outlined">info</span>
          <span>
            Going out will end the round and trigger scoring. Your team will receive a 100-point
            bonus for going out.
          </span>
        </p>
      </div>

      <!-- Action Buttons -->
      <div class="confirmation-actions">
        <button @click="handleCancel" class="btn-cancel">
          <span class="material-symbols-outlined">close</span>
          <span>Cancel</span>
        </button>
        <button
          @click="handleConfirm"
          :disabled="!allRequirementsMet"
          class="btn-confirm"
          :class="{ disabled: !allRequirementsMet }"
        >
          <span class="material-symbols-outlined">check_circle</span>
          <span>{{ allRequirementsMet ? 'Confirm Go Out' : 'Requirements Not Met' }}</span>
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseModal from './BaseModal.vue'

/**
 * GoOutConfirmationModal Component
 *
 * Confirms player wants to go out and shows requirements checklist.
 * Prevents going out if requirements not met.
 *
 * Props:
 * - team_canastas: { natural: number, mixed: number } - Team's canastas
 * - hand_card_count: Number of cards in hand + foot
 * - has_hidden_canasta: Whether player has unrevealed hidden canasta
 *
 * Emits:
 * - confirm: [] - Player confirmed go out
 * - cancel: [] - Player cancelled
 */

interface Props {
  isOpen: boolean
  data: {
    team_canastas?: {
      natural: number
      mixed: number
    }
    hand_card_count?: number
    has_hidden_canasta?: boolean
  } | null
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  data: null,
})

const emit = defineEmits<{
  confirm: []
  cancel: []
  close: []
}>()

/**
 * Team canastas
 */
const teamCanastas = computed(() => ({
  natural: props.data?.team_canastas?.natural || 0,
  mixed: props.data?.team_canastas?.mixed || 0,
}))

/**
 * Hand card count
 */
const handCardCount = computed(() => props.data?.hand_card_count || 0)

/**
 * Has hidden canasta
 */
const hasHiddenCanasta = computed(() => props.data?.has_hidden_canasta || false)

/**
 * Requirements status
 */
const requirements = computed(() => ({
  naturalCanastas: teamCanastas.value.natural >= 2,
  mixedCanastas: teamCanastas.value.mixed >= 2,
  emptyHand: handCardCount.value === 0,
  noHiddenCanasta: !hasHiddenCanasta.value,
}))

/**
 * All requirements met
 */
const allRequirementsMet = computed(() => {
  return (
    requirements.value.naturalCanastas &&
    requirements.value.mixedCanastas &&
    requirements.value.emptyHand &&
    requirements.value.noHiddenCanasta
  )
})

/**
 * Handle confirm
 */
function handleConfirm(): void {
  if (allRequirementsMet.value) {
    emit('confirm')
  }
}

/**
 * Handle cancel
 */
function handleCancel(): void {
  emit('cancel')
  emit('close')
}
</script>

<style scoped>
.go-out-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 0.5rem;
}

/* Warning Icon */
.warning-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(245, 158, 11, 0.2));
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse-warning 2s ease-in-out infinite;
}

.warning-icon .material-symbols-outlined {
  font-size: 3rem;
  color: #fbbf24;
}

@keyframes pulse-warning {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.4);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(251, 191, 36, 0);
  }
}

/* Main Message */
.main-message {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  line-height: 1.6;
  margin: 0;
  max-width: 500px;
}

/* Requirements Checklist */
.requirements-checklist {
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
}

.checklist-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 1.25rem;
}

.checklist-title .material-symbols-outlined {
  font-size: 1.25rem;
  color: #e8c547;
}

.requirement-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  margin-bottom: 0.75rem;
  transition: all 0.2s;
}

.requirement-item:last-child {
  margin-bottom: 0;
}

.requirement-item.met {
  background: rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.3);
}

.checkbox-icon {
  font-size: 1.75rem;
  flex-shrink: 0;
  color: rgba(239, 68, 68, 0.7);
}

.requirement-item.met .checkbox-icon {
  color: #10b981;
}

.requirement-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.requirement-label {
  font-size: 0.9375rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.requirement-status {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
}

.requirement-item.met .requirement-status {
  color: rgba(16, 185, 129, 0.8);
}

/* Warnings Section */
.warnings-section {
  width: 100%;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  padding: 1rem;
}

.warning-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: #f87171;
  font-size: 0.9375rem;
  line-height: 1.5;
}

.warning-item .material-symbols-outlined {
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

/* Confirmation Info */
.confirmation-info {
  width: 100%;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 12px;
  padding: 1rem;
}

.info-text {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: rgba(147, 197, 253, 0.9);
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
}

.info-text .material-symbols-outlined {
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

/* Action Buttons */
.confirmation-actions {
  display: flex;
  gap: 1rem;
  width: 100%;
  margin-top: 0.5rem;
}

.btn-cancel,
.btn-confirm {
  flex: 1;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
}

.btn-cancel {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.btn-confirm {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-confirm:hover:not(:disabled) {
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
  transform: translateY(-2px);
}

.btn-confirm:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.btn-confirm:disabled,
.btn-confirm.disabled {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.5);
  box-shadow: none;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-cancel .material-symbols-outlined,
.btn-confirm .material-symbols-outlined {
  font-size: 1.25rem;
}

/* Responsive */
@media (max-width: 640px) {
  .warning-icon {
    width: 64px;
    height: 64px;
  }

  .warning-icon .material-symbols-outlined {
    font-size: 2.5rem;
  }

  .main-message {
    font-size: 0.9375rem;
  }

  .requirements-checklist {
    padding: 1.25rem;
  }

  .requirement-item {
    padding: 0.875rem;
  }

  .confirmation-actions {
    flex-direction: column;
  }
}
</style>
